import React, { type FC, useState, useEffect } from 'react';

import { isBrowser } from '@lucid/utils';
import throttle from 'lodash/throttle';

import Box from '../../box/box';
import Container from '../../container/container';
import Icon from '../../icon/icon';
import Link from '../../link/link';
import Heading from '../../heading/heading';
import CtaWithSSO, { type SSOService } from '../../cta/cta-with-sso/cta-with-sso';

export interface FixedEmailBannerProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** Allows to select which SSO services to use */
  ssoServices?: SSOService[];
}

const FixedEmailBanner: FC<FixedEmailBannerProps> = ({ className, ssoServices, ...rest }) => {
  const [isActive, setIsActive] = useState(false);
  const [isActiveMobile, setIsActiveMobile] = useState(false);
  const [scrollDepth, setScrollDepth] = useState(0);
  const [isFullyScrolled, setIsFullyScrolled] = useState(false);
  const [shouldHideBanner, setShouldHideBanner] = useState(false);

  useEffect(() => {
    if (isBrowser()) {
      const handleScroll = throttle(() => {
        const prevScrollDepth = scrollDepth;
        const currentScrollDepth = window.scrollY;
        const pageHeight = document.body.scrollHeight - window.innerHeight;

        setScrollDepth(window.scrollY);
        setIsFullyScrolled(pageHeight < scrollDepth + 300);

        if (prevScrollDepth !== currentScrollDepth) {
          setIsActive(!isFullyScrolled);
        }
      }, 100);

      window.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [scrollDepth, isFullyScrolled]);

  if (shouldHideBanner) return <></>;

  return (
    <Container css={{ width: '100%' }}>
      <Box
        {...rest}
        css={theme => [
          {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndices.z1000,
            borderRadius: theme.radii.r16,
            padding: `${theme.space.s24}px ${theme.space.s32}px 0`,
            margin: `0 ${theme.space.s32}px`,
            background: theme.colors.coolGrey[0],
            transition: 'all .15s ease-out',
            display: 'grid',
            gap: theme.space.s24,
            alignItems: 'center',
            gridTemplateRows: 'repeat(3, auto)',
            transform: 'translateY(100%)',
            border: `1px solid ${theme.colors.coolGrey[20]}`,
            [theme.breakpoints.bp768.min]: {
              paddingBottom: theme.space.s24,
              width: 'min(90vw, 780px)',
              margin: '0 auto',
              gridTemplateRows: '40px',
              gridTemplateColumns: 'repeat(3, auto)',
            },
          },
          isActive && {
            [theme.breakpoints.bp768.min]: {
              transform: 'translateY(0)',
            },
          },
          isActiveMobile && {
            transform: 'translateY(0)',
            [theme.breakpoints.bp768.min]: {
              transform: isActive ? 'translateY(0)' : 'translateY(100%)',
            },
          },
        ]}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={className}
      >
        <Box
          css={theme => [
            {
              transform: `translateY(calc(-100% - ${theme.space.s40}px))`,
              transition: 'all .15s ease-out',
              position: 'relative',
              paddingTop: theme.space.s24,
              '&::before': {
                border: `1px solid ${theme.colors.coolGrey[20]}`,
                background: theme.colors.coolGrey[0],
                zIndex: theme.zIndices.zNegative1,
                content: '""',
                position: 'absolute',
                top: 0,
                left: `-${theme.space.s32}px`,
                height: `calc(100% + ${theme.space.s24}px)`,
                width: `calc(100% + ${theme.space.s64}px)`,
                borderTopLeftRadius: theme.radii.r16,
                borderTopRightRadius: theme.radii.r16,
              },

              display: 'flex',
              justifyContent: 'space-between',
              [theme.breakpoints.bp768.min]: {
                paddingTop: 'initial',
                transform: 'unset',
                alignSelf: 'start',
                '&::before': {
                  content: 'unset',
                },
              },
            },
            isFullyScrolled && { transform: 'translateY(100%)' },
            isActiveMobile && { transform: 'translateY(0)', padding: 0, '&::before': { border: 'unset' } },
          ]}
        >
          <Heading
            as="h2"
            css={theme => ({
              margin: `auto ${theme.space.s16}px auto 0`,
              fontWeight: 500,
              [theme.breakpoints.bp768.min]: { margin: `${theme.space.s4}px 0 auto 0` },
              [theme.breakpoints.bp900.min]: { fontSize: theme.fontSizes.fs32 },
            })}
            variant="headingL"
          >
            Start today
          </Heading>
          <Link
            asButton
            aria-label={`${isActiveMobile ? 'hide' : 'show'} email signup form`}
            css={theme => [
              {
                width: `calc(100% - ${theme.space.s48}px)`,
                height: '100%',
                position: 'absolute',
                left: 0,
                top: theme.space.s24,
                display: 'flex',
                justifyContent: 'flex-end',
                [theme.breakpoints.bp768.min]: {
                  display: 'none',
                  visibility: 'hidden',
                },
              },
              isActiveMobile && {
                top: 0,
              },
            ]}
            onClick={() => setIsActiveMobile(!isActiveMobile)}
            onFocus={() => setIsActive(true)}
            variant="basic"
          />
          <Link
            asButton
            aria-label="close sign-up banner"
            css={theme => ({ display: 'block', [theme.breakpoints.bp768.min]: { display: 'none' } })}
            onClick={() => setShouldHideBanner(true)}
            variant="basic"
          >
            <Icon type="X" />
          </Link>
        </Box>
        <CtaWithSSO
          css={theme => ({
            maxWidth: '100%',
            justifySelf: 'center',
            alignItems: 'center',
            [theme.breakpoints.bp768.min]: {
              display: 'flex',
              gap: theme.space.s16,
              width: 'auto',
              '& > p': {
                display: 'none',
              },
            },
          })}
          ssoSeparatorColor="coolGrey.100"
          ssoServices={ssoServices}
        />
        <Link
          asButton
          aria-label="close sign-up banner"
          css={theme => ({ display: 'none', [theme.breakpoints.bp768.min]: { display: 'block' } })}
          onClick={() => {
            setIsActive(false);

            setTimeout(() => {
              setShouldHideBanner(true);
            }, 150);
          }}
          variant="basic"
        >
          <Icon type="X" />
        </Link>
      </Box>
    </Container>
  );
};

export { FixedEmailBanner };
export default FixedEmailBanner;
